import React from 'react';
import classnames from 'classnames/bind';
import { useDebouncedValue } from '@tanstack/react-pacer';

import { Category } from 'types/category';

import { useCategories } from 'hooks/api/useCategories';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';

import {
  CategorySelect,
  HighlightedCategoryFilter,
} from 'components/CategorySelect';
import { QuerySearch } from 'components/QuerySearch/QuerySearch';
import {
  CatalogScope,
  CatalogScopeSwitch,
} from 'analytics-library/components/CatalogScopeSwitch/CatalogScopeSwitch';

import {
  PipelineTemplateGridContent,
  PipelineTemplateGridContentProps,
} from './Content';
import styles from './PipelineTemplateGrid.module.scss';

const c = classnames.bind(styles);

export type PipelineTemplateGridProps = Pick<
  PipelineTemplateGridContentProps,
  'filters' | 'onCreatePipeline' | 'onDeployPipeline'
> & {
  id: string;
  showPlanScopeFilter?: boolean;
};

export function PipelineTemplateGrid({
  id,
  filters,
  onCreatePipeline,
  onDeployPipeline,
  showPlanScopeFilter,
}: PipelineTemplateGridProps) {
  const [page, setPage] = React.useState(1);
  const [manualPlanScope, setManualPlanScope] = React.useState<CatalogScope>();
  const [totalElements, setTotalElements] = React.useState<number>();
  const [hasAutoSelected, setHasAutoSelected] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState(filters?.query ?? '');
  const [debouncedSearchInput] = useDebouncedValue(searchInput, { wait: 250 });

  const [highlightedCategories, setHighlightedCategories] = React.useState<
    Category[]
  >([]);
  const [moreCategories, setMoreCategories] = React.useState<Category[]>([]);

  const categories = [...highlightedCategories, ...moreCategories];

  // Get current plan information
  const {
    data: currentPlan,
    isRingPlan,
    isSimplifiedUxPlan,
  } = useCurrentPlan();

  // Fetch all categories to find matching plan category
  const { data: allCategories } = useCategories({
    params: {
      limit: 100, // Get all categories
    },
  });

  // Auto-select category matching plan name on component mount (only when simplified_ux is enabled)
  React.useEffect(() => {
    // Only auto-select once and only if user hasn't manually selected anything
    if (
      !hasAutoSelected &&
      currentPlan?.name &&
      allCategories?.data &&
      highlightedCategories.length === 0 &&
      moreCategories.length === 0
    ) {
      // Find category that matches the plan name (case-insensitive)
      const matchingCategory = allCategories.data.find(
        (category) =>
          category.label.toLowerCase() === currentPlan.name.toLowerCase()
      );

      if (matchingCategory) {
        setHasAutoSelected(true);
        // Check if it's a highlighted category or regular category
        if (matchingCategory.is_highlighted) {
          setHighlightedCategories([matchingCategory]);
        } else {
          setMoreCategories([matchingCategory]);
        }
      }
    }
  }, [
    currentPlan?.name,
    allCategories?.data,
    hasAutoSelected,
    highlightedCategories.length,
    moreCategories.length,
  ]);

  // Hide category filters when simplified_ux is enabled
  const shouldShowFilters = !isSimplifiedUxPlan;
  const hasSearchInput = Boolean(searchInput.trim());
  const planScope: CatalogScope = isRingPlan
    ? 'included'
    : (manualPlanScope ?? (hasSearchInput ? 'all' : 'included'));
  const onlyIncludedInPlan = isRingPlan || planScope === 'included';

  React.useEffect(() => {
    if (!manualPlanScope) {
      setPage(1);
    }
  }, [hasSearchInput, manualPlanScope]);

  function handlePlanScopeChange(value: CatalogScope) {
    setManualPlanScope(value);
    setPage(1);
  }

  const planScopeFilter =
    showPlanScopeFilter && !isRingPlan ? (
      <CatalogScopeSwitch
        id={`${id}_plan_scope`}
        includedLabel="Included templates only"
        allLabel="All templates"
        value={planScope}
        onChange={handlePlanScopeChange}
      />
    ) : undefined;

  return (
    <div className={c('wrap')}>
      {shouldShowFilters && (
        <div className={c('filters')}>
          <HighlightedCategoryFilter
            id={`${id}_filter`}
            value={highlightedCategories}
            onChange={(categories) => {
              setHighlightedCategories(categories);
              setHasAutoSelected(true); // Mark as user has made manual selection
              setPage(1);
            }}
          />
          <CategorySelect
            value={moreCategories}
            onChange={(values) => {
              setMoreCategories([...values]);
              setHasAutoSelected(true); // Mark as user has made manual selection
              setPage(1);
            }}
            queryFilters={{
              only_non_highlighted: true,
            }}
            isMulti
          />
        </div>
      )}

      <QuerySearch
        value={searchInput}
        entityName="template"
        searchLabel="Search templates..."
        totalElements={totalElements}
        onValueChange={(value) => {
          setSearchInput(value ?? '');
          setPage(1);
        }}
        actions={planScopeFilter}
      />

      <PipelineTemplateGridContent
        categories={categories}
        filters={{
          ...filters,
          query: debouncedSearchInput.trim() || undefined,
        }}
        onlyIncludedInPlan={onlyIncludedInPlan}
        page={page}
        onTotalElementsChange={setTotalElements}
        onPageChange={setPage}
        onCreatePipeline={onCreatePipeline}
        onDeployPipeline={onDeployPipeline}
      />
    </div>
  );
}
